<template>
  <section v-if="allowed" ref="returnTarget" tabindex="-1" class="text-left" :class="{ 'w-100': deliveryActive }" :aria-label="$t(`${copyKey}.title`)">
    <v-btn ref="trigger" color="primary" variant="tonal" prepend-icon="mdi-email-search-outline" :disabled="!selectionValid || deliveryActive" @click="open = true">
      {{ $t(`${copyKey}.title`) }}
    </v-btn>
    <v-snackbar v-model="success" color="info">{{ $t(`${sendKey}.started`) }}</v-snackbar>
    <NcrmPrivacyBulkProgress ref="progress" :state="delivery" @retry="dispatcher.retryStatus()" @dismiss="dismiss" />
    <v-overlay v-model="open" :z-index="3210" scroll-strategy="block" class="privacy-bulk-preview-overlay">
      <v-navigation-drawer v-model="open" location="right" temporary :scrim="false" :width="620" class="privacy-bulk-preview"
        role="dialog" :aria-modal="open" :aria-label="$t(`${copyKey}.title`)">
        <div v-if="open" class="pa-5 d-flex flex-column ga-4" @keydown.tab="trapFocus">
          <div class="d-flex align-center justify-space-between ga-3">
            <h3 class="text-h6">{{ $t(`${copyKey}.title`) }}</h3>
            <v-btn ref="close" icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="open = false" />
          </div>
          <p class="text-body-2 mb-0">{{ $t(`${copyKey}.hint`) }}</p>
          <v-alert v-if="!selectionValid" type="warning">{{ $t(`${copyKey}.limit`) }}</v-alert>
          <v-alert v-else-if="denied" type="warning">{{ $t('ncrm.contacts.dataProtection.record.noPermission') }}</v-alert>
          <v-skeleton-loader v-else-if="loading" type="list-item-two-line@3" />
          <template v-else>
            <v-alert v-if="delivery.error" type="warning" role="status">{{ $t(`${sendKey}.${delivery.error}`) }}</v-alert>
            <v-alert v-if="error" type="error">
              {{ $t('ncrm.contacts.dataProtection.record.invitation.loadError') }}
              <v-btn variant="text" @click="loadOptions">{{ $t('ncrm.contacts.dataProtection.record.retry') }}</v-btn>
            </v-alert>
            <v-alert v-if="!purposes.length && !error" type="info">{{ $t('ncrm.contacts.dataProtection.record.invitation.emptyPurpose') }}</v-alert>
            <v-select v-model="form.purpose_id" :items="purposes" item-title="name" item-value="id"
              :label="$t('ncrm.contacts.dataProtection.record.invitation.purpose')" :menu-props="{ zIndex: 3220 }" @update:model-value="usePurposeDefault" />
            <NcrmPrivacyPurposeLink :purpose-id="form.purpose_id" :return-to="$route.fullPath" target="_blank" rel="noopener" />
            <v-select v-model="form.locale" :items="locales.map(value => ({ title: value.toUpperCase(), value }))"
              :label="$t('ncrm.contacts.dataProtection.record.invitation.language')" :menu-props="{ zIndex: 3220 }" />
            <v-checkbox v-model="form.doi_after_accept" :label="$t('ncrm.contacts.dataProtection.record.invitation.doi')" hide-details />
            <router-link v-if="manager" :to="noticesLink" target="_blank" rel="noopener">{{ $t('ncrm.contacts.dataProtection.record.invitation.notices') }}</router-link>
            <router-link v-if="smtpAllowed" to="/ncrm/data-integrations?tab=smtp" target="_blank" rel="noopener">{{ $t('ncrm.contacts.dataProtection.record.doiResend.configureSmtp') }}</router-link>
            <v-btn color="primary" :loading="checking" :disabled="!canPreview" @click="loadPreview">{{ $t(`${copyKey}.preview`) }}</v-btn>
            <v-alert v-if="previewError" type="error">{{ $t(`${copyKey}.error`) }}</v-alert>
            <template v-if="preview">
              <p role="status" aria-live="polite">{{ $t(`${copyKey}.summary`, preview.summary) }}</p>
              <v-checkbox v-if="preview.summary.eligible > 0" v-model="confirmed" :label="$t(`${sendKey}.confirm`, { count: preview.summary.eligible })" :disabled="delivery.hold" hide-details />
              <v-btn color="primary" :loading="delivery.sending" :disabled="!canSend" @click="send">{{ $t(`${sendKey}.send`) }}</v-btn>
              <div v-for="(row, index) in preview.rows" :key="index" class="preview-row py-3">
                <strong>{{ recordName(row.subject_id) }}</strong>
                <div>{{ row.value_display || $t(`${copyKey}.no_email`) }}</div>
                <div class="d-flex align-center ga-2 mt-1">
                  <v-icon size="18" :color="row.eligible ? 'success' : 'warning'">{{ row.eligible ? 'mdi-check-circle-outline' : 'mdi-cancel' }}</v-icon>
                  <span>{{ row.eligible ? $t(`${copyKey}.eligible`) : reasonText(row) }}</span>
                </div>
                <p v-if="row.warning" class="text-body-2 mt-2 mb-0">{{ $t('ncrm.contacts.dataProtection.record.invitation.warning') }}</p>
              </div>
            </template>
          </template>
        </div>
      </v-navigation-drawer>
    </v-overlay>
  </section>
</template>

<script>
import axios from "axios";
import NcrmPrivacyPurposeLink from "@/components/ncrm/privacy/NcrmPrivacyPurposeLink.vue";
import { hasPrivacyManagerRole, hasPrivacyRegistryRole } from "@/Utile/ncrmPrivacyAccess.mjs";
import { privacyNoticesRoute } from "@/Utile/ncrmExternalFormPrivacy.mjs";
import { invitationReasonText } from "@/Utile/ncrmPrivacyInvitationReasons.mjs";
import { bulkDeliveryState, createBulkDelivery } from "@/Utile/ncrmPrivacyBulkDelivery.mjs";
import NcrmPrivacyBulkProgress from "./NcrmPrivacyBulkProgress.vue";
import { canAccessCompanySmtpWorkspace } from "@/Utile/ncrmMitpSync.mjs";

// Preview stays consultative; dispatch uses the existing server-owned worker.
export default {
  name: "NcrmPrivacyBulkPreviewAction",
  components: { NcrmPrivacyPurposeLink, NcrmPrivacyBulkProgress },
  emits: ["active", "sent"],
  props: { ids: { type: Array, required: true }, records: { type: Array, default: () => [] },
    subjectType: { type: String, required: true } },
  data: () => ({ open: false, allowed: false, manager: false, loading: false, checking: false,
    error: false, previewError: false, denied: false, purposes: [], locales: [], form: {}, preview: null, generation: 0, sequence: 0,
    delivery: bulkDeliveryState(), dispatcher: null, confirmed: false, success: false, smtpAllowed: false }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.bulkPreview"; },
    sendKey() { return "ncrm.contacts.dataProtection.bulkSend"; },
    deliveryActive() { return this.delivery.hold || !!this.delivery.job; },
    canSend() { return this.canPreview && this.confirmed && this.preview?.summary.eligible > 0 && !this.deliveryActive; },
    selectionValid() { return ["person", "company"].includes(this.subjectType) && this.ids.length > 0 && this.ids.length <= 100
      && this.ids.every(id => (typeof id === "number" || (typeof id === "string" && /^\d+$/.test(id)))
        && Number.isSafeInteger(Number(id)) && Number(id) > 0); },
    selectionKey() { return JSON.stringify([this.subjectType, this.ids]); },
    params() { return { subject_type: this.subjectType, subject_ids: this.ids.map(Number), ...this.form }; },
    choiceKey() { return JSON.stringify(this.params); },
    canPreview() { return this.open && this.allowed && this.selectionValid && !this.deliveryActive && !this.loading && !this.checking && !this.error && !this.denied
      && this.purposes.some(p => p.id === this.form.purpose_id) && this.locales.includes(this.form.locale); },
    noticesLink() { return privacyNoticesRoute({ purposeId: this.form.purpose_id, locale: this.form.locale, returnTo: this.$route.fullPath }); },
  },
  created() {
    this.allowed = hasPrivacyRegistryRole(); this.manager = hasPrivacyManagerRole();
    this.smtpAllowed = canAccessCompanySmtpWorkspace();
    this.dispatcher = createBulkDelivery({ state: this.delivery, request: axios, completed: () => this.$emit("sent") });
  },
  mounted() { window.addEventListener("focus", this.resume); },
  beforeUnmount() { this.dispatcher?.dispose(); this.reset(); window.removeEventListener("focus", this.resume); },
  watch: {
    deliveryActive(value) { this.$emit("active", value); },
    selectionKey() { this.open = false; this.reset(); },
    choiceKey() { this.sequence += 1; this.confirmed = false; this.preview = null; this.previewError = false; this.checking = false; },
    open(value) {
      this.reset();
      if (value) { this.loadOptions(); this.$nextTick(() => this.$refs.close?.$el?.focus()); }
      else this.$nextTick(() => { if (!this.open) (this.deliveryActive ? this.$refs.progress : this.$refs.trigger?.$el || this.$refs.returnTarget)?.focus({ preventScroll: true }); });
    },
  },
  methods: {
    dismiss() { this.dispatcher.dismiss(); this.$nextTick(() => this.$refs.returnTarget?.focus({ preventScroll: true })); },
    reset() {
      this.generation += 1; this.sequence += 1; this.confirmed = false; this.preview = null; this.form = {}; this.purposes = []; this.locales = [];
      this.loading = false; this.checking = false; this.error = false; this.previewError = false; this.denied = false;
    },
    async resume() { if (this.open) { await this.loadOptions(); if (this.canPreview) await this.loadPreview(); } },
    usePurposeDefault() { this.form.doi_after_accept = this.purposes.find(p => p.id === this.form.purpose_id)?.double_opt_in_enabled === true; },
    async loadOptions() {
      if (!this.open || !this.allowed || !this.selectionValid) return;
      const generation = ++this.generation, key = this.selectionKey;
      this.sequence += 1; this.preview = null; this.loading = true; this.checking = false; this.error = false; this.previewError = false;
      try {
        const { data } = await axios.get("/ncrm/privacy/invitations/options", {
          params: { subject_type: this.subjectType, subject_id: Number(this.ids[0]) }, timeout: 15000 });
        if (generation !== this.generation || key !== this.selectionKey || !this.open) return;
        this.purposes = data.purposes; this.locales = data.locales; this.denied = false;
        const selected = this.purposes.some(p => p.id === this.form.purpose_id);
        this.form = { purpose_id: selected ? this.form.purpose_id : this.purposes.length === 1 ? this.purposes[0].id : null,
          locale: this.locales.includes(this.form.locale) ? this.form.locale : this.locales.includes(this.$i18n.locale) ? this.$i18n.locale : "ro",
          doi_after_accept: selected ? this.form.doi_after_accept : false };
        if (!selected) this.usePurposeDefault();
      } catch (error) { if (generation === this.generation) { this.purposes = []; this.denied = [401, 403].includes(error.response?.status); this.error = !this.denied; } }
      finally { if (generation === this.generation) this.loading = false; }
    },
    async loadPreview() {
      if (!this.canPreview) return;
      const generation = this.generation, sequence = ++this.sequence, key = this.choiceKey;
      this.confirmed = false; this.preview = null; this.previewError = false; this.checking = true;
      try {
        const { data } = await axios.post("/ncrm/privacy/invitations/bulk/preview", this.params, { timeout: 45000 });
        if (generation === this.generation && sequence === this.sequence && key === this.choiceKey && this.open) this.preview = data;
      } catch (error) {
        if (generation === this.generation && sequence === this.sequence && key === this.choiceKey && this.open) {
          this.denied = [401, 403].includes(error.response?.status); this.previewError = !this.denied;
          if (this.denied) { this.purposes = []; this.locales = []; this.form = {}; }
        }
      } finally { if (generation === this.generation && sequence === this.sequence) this.checking = false; }
    },
    async send() {
      if (!this.canSend) return;
      const key = this.choiceKey;
      const started = await this.dispatcher.start(this.params);
      if (started) {
        if (key === this.choiceKey) this.open = false;
        this.success = true; this.$emit("sent"); this.$nextTick(() => this.$refs.progress?.focus());
      } else if (key === this.choiceKey) { this.confirmed = false; this.preview = null; }
    },
    recordName(id) { return this.records.find(record => Number(record.id) === Number(id))?.name || `#${id}`; },
    reasonText(row) {
      return ["no_email", "duplicate_email"].includes(row.reason) ? this.$t(`${this.copyKey}.${row.reason}`)
        : invitationReasonText(this, row.reason, row.limits);
    },
    trapFocus(event) {
      const nodes = [...event.currentTarget.querySelectorAll('button,a,input,[tabindex="0"]')].filter(node => !node.disabled && node.getClientRects().length);
      if (document.activeElement === (event.shiftKey ? nodes[0] : nodes.at(-1))) {
        event.preventDefault(); (event.shiftKey ? nodes.at(-1) : nodes[0])?.focus();
      }
    },
  },
};
</script>

<style scoped>
.privacy-bulk-preview-overlay :deep(.v-overlay__content) { position: fixed; inset: 0; pointer-events: none; }
.privacy-bulk-preview { z-index: 3210 !important; max-width: 100vw; text-align: left; pointer-events: auto; }
.privacy-bulk-preview :deep(.v-navigation-drawer__content) { overscroll-behavior: contain; }
.preview-row { border-top: 1px solid var(--ncrm-color-border-subtle); overflow-wrap: anywhere; }
section:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); }
</style>
